import { useCallback } from 'react';
import { MutationResponse } from 'global';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { TableFilter } from '@trnsact/trnsact-shared-types';

import { opportunitiesListApi } from './index';

type CreateTableFilterDto = Pick<TableFilter, 'name' | 'config' | 'isDefault' | 'tableType'>;
type UpdateTableFilterDto = Pick<TableFilter, 'name' | 'config' | 'tableFilterId'>;

export function usePresets() {
  const { showNotification } = useNotifications();

  const [onCreate, { loading: isFilterCreating }] = useMutation<
    {
      tableFilter: MutationResponse<void>;
    },
    {
      input: CreateTableFilterDto;
    }
  >(opportunitiesListApi.createFilter, {
    refetchQueries: [opportunitiesListApi.getTableFilters],
    awaitRefetchQueries: true,
  });

  const handleCreateFilter = useCallback(
    async (dto: CreateTableFilterDto) => {
      try {
        const { data } = await onCreate({ variables: { input: dto } });

        if (!data?.tableFilter?.result?.success) {
          showNotification(data?.tableFilter?.result?.error ?? 'Filter not created', { type: 'error' });
          return;
        }

        showNotification('Filter successfully created!');
      } catch (error: any) {
        showNotification(error?.message ?? 'Filter not created', { type: 'error' });
      }
    },
    [onCreate, showNotification]
  );

  const [onUpdate, { loading: isFilterUpdating }] = useMutation<
    {
      tableFilter: MutationResponse<void>;
    },
    {
      input: UpdateTableFilterDto;
    }
  >(opportunitiesListApi.updateFilter, {
    refetchQueries: [opportunitiesListApi.getTableFilters],
    awaitRefetchQueries: true,
  });

  const handleUpdateFilter = useCallback(
    async (dto: UpdateTableFilterDto) => {
      try {
        const { data } = await onUpdate({ variables: { input: dto } });

        if (!data?.tableFilter?.result?.success) {
          showNotification(data?.tableFilter?.result?.error ?? 'Filter not updated', { type: 'error' });
          return;
        }

        showNotification('Filter successfully updated!');
      } catch (error: any) {
        showNotification(error?.message ?? 'Filter not updated', { type: 'error' });
      }
    },
    [onUpdate, showNotification]
  );

  const [onDelete, { loading: isFilterDeleting }] = useMutation<{ tableFilter: MutationResponse<void> }>(
    opportunitiesListApi.deleteFilter,
    {
      refetchQueries: [opportunitiesListApi.getTableFilters],
      awaitRefetchQueries: true,
    }
  );

  const handleDeleteFilter = useCallback(
    async (id: string) => {
      try {
        const result = await onDelete({ variables: { tableFilterId: id } });

        result?.data?.tableFilter?.result?.success
          ? showNotification('Filter successfully deleted!')
          : showNotification('Filter not deleted', { type: 'error' });

        return result?.data?.tableFilter?.result;
      } catch (error: any) {
        showNotification(error?.message ?? 'Filter not deleted', { type: 'error' });
      }
    },
    [onDelete, showNotification]
  );

  return {
    handleCreateFilter,
    isFilterCreating,
    handleUpdateFilter,
    isFilterUpdating,
    handleDeleteFilter,
    isFilterDeleting,
  };
}
